import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { listRuns, type RunStatus, type RunStatusReadable } from '../api/runs';
import { formatDate } from '../lib/format';

// STATUS_OPTIONS drives the filter dropdown, which POSTs `?status=...`
// back to the server — that's a WRITE path, so we intentionally use
// strict RunStatus here. 'unknown' is never a valid filter value.
const STATUS_OPTIONS: Array<{ value: RunStatus | ''; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'running', label: 'Running' },
  { value: 'done', label: 'Done' },
  { value: 'failed', label: 'Failed' },
  { value: 'needs_attention', label: 'Needs attention' },
  { value: 'cancelled', label: 'Cancelled' },
];

// STATUS_COLORS keys the display badge — READ path, so it must handle
// the 'unknown' sentinel that ships with `RunStatusReadable` (#482).
const STATUS_COLORS: Record<RunStatusReadable, string> = {
  queued: 'bg-gray-100 text-gray-700',
  starting: 'bg-blue-100 text-blue-700',
  running: 'bg-blue-100 text-blue-700',
  done: 'bg-green-100 text-green-800',
  needs_attention: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-600',
  paused_budget: 'bg-orange-100 text-orange-800',
  time_cap_reached: 'bg-orange-100 text-orange-800',
  unknown: 'bg-gray-100 text-gray-500',
};

interface RunRowProps {
  r: {
    runId: string;
    userId: string;
    status: RunStatusReadable;
    counters: { jobsApplied: number; externalAppliesUnknown?: number };
    startedAt: string;
  };
  onNavigate: (id: string) => void;
}

export function RunRow({ r, onNavigate }: RunRowProps): React.ReactElement {
  const unknownAts = r.counters?.externalAppliesUnknown ?? 0;
  return (
    <tr onClick={() => onNavigate(r.runId)} className="hover:bg-surface-subtle cursor-pointer">
      <td className="px-4 py-3 font-mono text-xs text-text-muted">{r.runId.slice(0, 24)}…</td>
      <td className="px-4 py-3 font-mono text-xs text-text-muted">{r.userId.slice(0, 12)}…</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[r.status]}`}>
            {r.status}
          </span>
          {unknownAts > 0 && (
            <Link
              to="/improvements/unknown-ats"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200"
            >
              ⚠ {unknownAts} unknown ATS
            </Link>
          )}
        </div>
      </td>
      <td className="px-4 py-3">{r.counters?.jobsApplied ?? 0}</td>
      <td className="px-4 py-3 text-text-muted">{formatDate(r.startedAt)}</td>
    </tr>
  );
}

export function RunsList(): React.ReactElement {
  const [status, setStatus] = useState<RunStatus | ''>('');
  const [cursor, setCursor] = useState<string | undefined>();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['runs', { status, cursor }],
    queryFn: () => listRuns({ status: status || undefined, cursor, limit: 50 }),
    staleTime: 30_000,
  });

  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-text-primary">Runs</h1>
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value as RunStatus | ''); setCursor(undefined); }}
          className="text-sm border border-border rounded-md px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {isLoading && <p className="text-sm text-text-muted">Loading…</p>}

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-subtle border-b border-border">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Run ID</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">User</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Status</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Jobs Applied</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Started</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((r) => (
              <RunRow key={r.runId} r={r} onNavigate={(id) => navigate(`/runs/${id}`)} />
            ))}
            {!isLoading && items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-text-muted">No runs found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data?.nextCursor && (
        <button onClick={() => setCursor(data.nextCursor ?? undefined)} className="text-sm text-brand-600 hover:underline">
          Next page →
        </button>
      )}
    </div>
  );
}
